/* eslint-disable */
// ============================================================
//  LANDING SHARED — helpers et composants partagés par les 3
//  versions de landing (V1 cinématique, V2 immersive, V3 démo).
//
//  Extraits de landing-v2.jsx (qui passait de 1769 à ~250 lignes
//  après ce découpage). Aucun changement de comportement —
//  juste un split de fichier pour réduire le bundle initial
//  (chaque version est désormais lazy-loadée séparément).
// ============================================================

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { T, loadLang } from "./constants";
import { supabase } from "./supabase";

// === Helper : Big Language Picker (drapeaux en ligne, plus visible) ===
// 6 boutons drapeau + code côte à côte. Plus accessible et plus visible
// que le LangPicker dropdown — un seul clic pour changer de langue.
export function BigLangPicker({ currentLang, onChange, dark = false }) {
  const languages = [
    { code: "fr", flag: "🇫🇷", name: "FR" },
    { code: "en", flag: "🇬🇧", name: "EN" },
    { code: "de", flag: "🇩🇪", name: "DE" },
    { code: "it", flag: "🇮🇹", name: "IT" },
    { code: "es", flag: "🇪🇸", name: "ES" },
    { code: "pt", flag: "🇵🇹", name: "PT" },
    { code: "nl", flag: "🇳🇱", name: "NL" },
  ];
  return (
    <motion.div
      animate={{ boxShadow: [
        "0 0 0 0 rgba(200,0,26,0)",
        "0 0 22px 4px rgba(200,0,26,.55)",
        "0 0 0 0 rgba(200,0,26,0)",
      ] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: 6,
      padding: "6px 8px",
      background: dark ? "rgba(255,255,255,.05)" : "rgba(255,255,255,.7)",
      backdropFilter: "blur(10px)",
      border: dark ? "1px solid rgba(255,255,255,.1)" : "1px solid var(--border)",
      borderRadius: 100,
    }}>
      {languages.map(l => {
        const active = l.code === currentLang;
        return (
          <motion.button
            key={l.code}
            onClick={() => onChange(l.code)}
            whileTap={{ scale: 0.94 }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "7px 11px",
              background: active
                ? "var(--app-cta-bg)"
                : "transparent",
              border: "none",
              borderRadius: 100,
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "var(--fh)",
              color: active ? "#fff" : (dark ? "rgba(255,255,255,.7)" : "var(--text)"),
              cursor: "pointer",
              boxShadow: active ? "0 4px 14px rgba(200,0,26,.4)" : "none",
              transition: "color .2s, background .2s",
            }}
          >
            <span style={{ fontSize: 14 }}>{l.flag}</span>
            <span>{l.name}</span>
          </motion.button>
        );
      })}
    </motion.div>
  );
}

export function LangPicker({ currentLang, onChange, dark = false }) {
  const [open, setOpen] = useState(false);
  const languages = [
    { code: "fr", flag: "🇫🇷", name: "FR" },
    { code: "en", flag: "🇬🇧", name: "EN" },
    { code: "de", flag: "🇩🇪", name: "DE" },
    { code: "it", flag: "🇮🇹", name: "IT" },
    { code: "es", flag: "🇪🇸", name: "ES" },
    { code: "pt", flag: "🇵🇹", name: "PT" },
    { code: "nl", flag: "🇳🇱", name: "NL" },
  ];
  const current = languages.find(l => l.code === currentLang) || languages[0];
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: dark ? "rgba(255,255,255,.1)" : "rgba(255,255,255,.95)",
          backdropFilter: "blur(10px)",
          border: dark ? "1px solid rgba(255,255,255,.18)" : "1px solid var(--border)",
          borderRadius: 100,
          padding: "8px 14px",
          fontSize: 13,
          fontWeight: 700,
          color: dark ? "#fff" : "var(--text)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 16 }}>{current.flag}</span>
        <span>{current.name}</span>
        <span style={{ fontSize: 9, opacity: 0.6 }}>▼</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              right: 0,
              background: "#fff",
              borderRadius: 14,
              padding: 6,
              boxShadow: "0 8px 30px rgba(0,0,0,.18)",
              border: "1px solid var(--border)",
              minWidth: 160,
              zIndex: 200,
            }}
          >
            {languages.map(l => (
              <button
                key={l.code}
                onClick={() => { onChange(l.code); setOpen(false); }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "9px 12px",
                  background: l.code === currentLang ? "rgba(200,0,26,.08)" : "transparent",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: l.code === currentLang ? 700 : 500,
                  color: "var(--text)",
                }}
              >
                <span style={{ fontSize: 18 }}>{l.flag}</span>
                <span>{l.name === "FR" ? "Français" : l.name === "EN" ? "English" : l.name === "DE" ? "Deutsch" : l.name === "IT" ? "Italiano" : l.name === "ES" ? "Español" : "Português"}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// === Helper : count-up animé (rAF + easeOutQuart) ===
// Anime de 0 vers `target` en `duration` ms. Renvoie 0 tant que target est null.
export function useCountUp(target, duration = 1500) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === null || target === undefined) { setValue(0); return; }
    if (target === 0) { setValue(0); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4); // easeOutQuart
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

// === Helper : Social Proof Bar (réutilisé dans les 3 versions) ===
// Design adulte/minimal : un petit dot vert pulsant (signal "live")
// + le compteur animé en gras + le sous-texte explicatif.
// Plus de pastilles emojis — épuré, premium.
export function SocialProofBar({ dark = false, signupCount, t }) {
  // IMPORTANT : tous les hooks doivent être appelés AVANT tout early return.
  // useCountUp accepte null (il renvoie 0 dans ce cas).
  const animated = useCountUp(signupCount, 1500);

  // Pendant le chargement (null) : on n'affiche rien pour éviter le flicker
  if (signupCount === null || signupCount === undefined) return null;

  // Texte adaptatif selon l'avancement réel de la communauté (basé sur la cible)
  const isEmpty = signupCount === 0;
  const isFew = signupCount > 0 && signupCount < 10;
  const isGrowing = signupCount >= 10 && signupCount < 100;
  const mainText = isEmpty
    ? t.spFirstRider
    : isFew
      ? `${animated} ${signupCount === 1 ? t.spPioneer : t.spPioneers}`
      : isGrowing
        ? `${animated} ${t.spRiders}`
        : `${animated}+ ${t.spRiders}`;
  const subText = isEmpty
    ? t.spFirstRiderSub
    : isFew
      ? t.spPioneerSub
      : t.spRidersSub;

  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 11,
      padding: "8px 16px 8px 13px",
      background: dark ? "rgba(255,255,255,.06)" : "rgba(255,255,255,.7)",
      backdropFilter: "blur(10px)",
      border: dark ? "1px solid rgba(255,255,255,.1)" : "1px solid var(--border)",
      borderRadius: 100,
    }}>
      {/* Dot "live" : signale que le chiffre est du vrai temps réel, pas un placeholder */}
      <span style={{ position: "relative", display: "inline-flex", width: 8, height: 8 }}>
        <motion.span
          animate={{ opacity: [0.6, 0, 0.6], scale: [1, 2.2, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute", inset: 0,
            borderRadius: "50%",
            background: "#22c55e",
          }}
        />
        <span style={{
          position: "relative",
          width: 8, height: 8,
          borderRadius: "50%",
          background: "#22c55e",
          boxShadow: "0 0 6px rgba(34,197,94,.7)",
        }} />
      </span>
      <div style={{ fontSize: 12, color: dark ? "rgba(255,255,255,.9)" : "var(--text)", lineHeight: 1.3 }}>
        <div style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{mainText}</div>
        <div style={{ fontSize: 10, opacity: 0.65 }}>{subText}</div>
      </div>
    </div>
  );
}

// === Helper commun : récupère lang + traductions ===
export function useLandingI18n() {
  const detectBrowserLang = () => {
    try {
      const browserLang = (navigator.language || "en").substring(0, 2).toLowerCase();
      if (["fr", "en", "de", "it", "es", "pt", "nl"].includes(browserLang)) return browserLang;
    } catch (e) {}
    return "en";
  };
  const getInitialLang = () => {
    try {
      const saved = localStorage.getItem("hc_lang");
      if (saved && ["fr", "en", "de", "it", "es", "pt", "nl"].includes(saved)) return saved;
    } catch (e) {}
    return detectBrowserLang();
  };
  const [lang, setLang] = useState(getInitialLang());
  const tLang = T[lang] || T.fr;
  const t = tLang.landing || T.fr.landing;

  // ⚠️ La landing tient son PROPRE état de langue, indépendant de celui d'App.jsx.
  // Depuis que les traductions sont chargées à la demande (29 août 2026), il faut
  // donc charger la langue ici aussi — sinon un visiteur qui choisit l'allemand
  // dans le sélecteur verrait du français en silence, sur le tout premier écran.
  const [, setI18nTick] = useState(0);
  useEffect(() => {
    let vivant = true;
    loadLang(lang).finally(() => { if (vivant) setI18nTick(n => n + 1); });
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLang = (code) => {
    // On charge AVANT de poser la langue : sinon le rendu passerait par le repli
    // français puis rebasculerait, ce qui se voit à l'écran.
    loadLang(code).finally(() => setLang(code));
    try { localStorage.setItem("hc_lang", code); } catch (e) {}
  };
  return { lang, t, tLang, changeLang };
}

// === Helper : stats publiques de la landing via RPC ===
// Retourne null pendant le chargement, puis { riders, ads, stables } (integers).
// En cas d'erreur, retourne un objet avec 0 partout (dégradation propre).
export function useLandingStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("public_landing_stats");
        if (!alive) return;
        if (error || !data || typeof data !== "object") {
          setStats({ riders: 0, ads: 0, stables: 0 });
          return;
        }
        setStats({
          riders:  typeof data.riders  === "number" ? data.riders  : 0,
          ads:     typeof data.ads     === "number" ? data.ads     : 0,
          stables: typeof data.stables === "number" ? data.stables : 0,
        });
      } catch (e) {
        if (alive) setStats({ riders: 0, ads: 0, stables: 0 });
      }
    })();
    return () => { alive = false; };
  }, []);
  return stats;
}

// === Helper : Mini-dashboard live ===
// Rotation auto entre les stats > 0 (toutes les 3,2 s), avec count-up animé
// au premier rendu. Si toutes les stats valent 0, le composant ne rend rien.
export function LiveDashboard({ stats, t, dark = true }) {
  // Construit la liste des stats affichables (on filtre les 0 pour rester crédible)
  const items = useMemo(() => {
    if (!stats) return [];
    const list = [];
    if (stats.riders > 0)  list.push({ key: "riders",  value: stats.riders,  label: t.spRiders });
    if (stats.ads > 0)     list.push({ key: "ads",     value: stats.ads,     label: t.spLiveAds });
    if (stats.stables > 0) list.push({ key: "stables", value: stats.stables, label: t.spLiveStables });
    return list;
  }, [stats, t]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    const id = setInterval(() => setIdx(i => (i + 1) % items.length), 3200);
    return () => clearInterval(id);
  }, [items.length]);

  // IMPORTANT : tous les hooks doivent être appelés AVANT tout early return.
  // current peut être undefined si la liste est vide → useCountUp accepte null.
  const current = items.length > 0 ? items[idx % items.length] : null;
  const animated = useCountUp(current ? current.value : null, 1200);

  if (!stats || !current) return null;

  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: "7px 14px",
      background: dark ? "rgba(255,255,255,.05)" : "rgba(255,255,255,.7)",
      backdropFilter: "blur(10px)",
      border: dark ? "1px solid rgba(255,255,255,.1)" : "1px solid var(--border)",
      borderRadius: 100,
      fontSize: 12,
      color: dark ? "rgba(255,255,255,.85)" : "var(--text)",
    }}>
      {/* Dot rouge pulsant (style live broadcast) */}
      <span style={{ position: "relative", display: "inline-flex", width: 7, height: 7 }}>
        <motion.span
          animate={{ opacity: [0.5, 0, 0.5], scale: [1, 2.4, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#ef4444" }}
        />
        <span style={{
          position: "relative", width: 7, height: 7, borderRadius: "50%",
          background: "#ef4444", boxShadow: "0 0 6px rgba(239,68,68,.7)",
        }} />
      </span>
      <AnimatePresence mode="wait">
        <motion.span
          key={current.key}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.35 }}
          style={{ display: "inline-flex", alignItems: "baseline", gap: 5 }}
        >
          <strong style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{animated}</strong>
          <span style={{ opacity: 0.85 }}>{current.label}</span>
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

// === Helper : Étincelles cinéma en fond ===
// 32 étincelles plus visibles et scintillantes — effet "magie nocturne".
// Mouvement vertical lent + twinkle (scale/opacity oscille rapidement).
// 3 palettes : blanc lumineux, rouge HC, doré chaud.
export function NightParticles() {
  const particles = useMemo(() =>
    Array.from({ length: 32 }, (_, i) => {
      const palette = Math.random();
      const color = palette < 0.55
        ? { fill: "rgba(255,255,255,1)", glow: "rgba(255,255,255,.85)" } // blanc 55 %
        : palette < 0.85
          ? { fill: "rgba(255,90,110,1)", glow: "rgba(255,90,110,.9)" }   // rouge HC 30 %
          : { fill: "rgba(255,210,120,1)", glow: "rgba(255,210,120,.9)" }; // doré 15 %
      return {
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 14,
        duration: 14 + Math.random() * 10,
        size: 2.5 + Math.random() * 3.5,
        baseOpacity: 0.6 + Math.random() * 0.4,
        twinkleSpeed: 1.2 + Math.random() * 1.4,
        fill: color.fill,
        glow: color.glow,
      };
    }), []);
  return (
    <div style={{
      position: "absolute", inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 0,
    }}>
      {particles.map(p => (
        <motion.div
          key={p.id}
          initial={{ y: "110vh" }}
          animate={{ y: "-10vh" }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            position: "absolute",
            left: `${p.x}%`,
            width: p.size,
            height: p.size,
          }}
        >
          <motion.div
            animate={{
              opacity: [p.baseOpacity * 0.3, p.baseOpacity, p.baseOpacity * 0.3],
              scale: [0.7, 1.2, 0.7],
            }}
            transition={{
              duration: p.twinkleSpeed,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background: p.fill,
              boxShadow: `0 0 ${p.size * 3}px ${p.glow}, 0 0 ${p.size * 6}px ${p.glow}`,
            }}
          />
        </motion.div>
      ))}
    </div>
  );
}

// === Helper : FAQ accordéon ===
// Sélectionne 5 questions pré-existantes du gros bloc faqQ1..20 (par langue).
// Un seul item ouvert à la fois. Réponse qui slide-down + fade.
export function LandingFAQ({ t, tLang }) {
  const [openIdx, setOpenIdx] = useState(0);
  const items = [
    { q: tLang.faqQ16, a: tLang.faqA16 },
    { q: tLang.faqQ20, a: tLang.faqA20 },
    { q: tLang.faqQ13, a: tLang.faqA13 },
    { q: tLang.faqQ18, a: tLang.faqA18 },
    { q: tLang.faqQ17, a: tLang.faqA17 },
  ].filter(it => it.q && it.a);

  if (items.length === 0) return null;

  return (
    <div style={{
      width: "100%",
      maxWidth: 420,
      margin: "0 auto",
      padding: "20px 18px",
      background: "rgba(255,255,255,.04)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(255,255,255,.08)",
      borderRadius: 18,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700,
        letterSpacing: 1.4,
        textTransform: "uppercase",
        color: "rgba(255,255,255,.65)",
        marginBottom: 14,
        textAlign: "center",
      }}>
        ❓ {t.spFaqTitle}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((item, i) => {
          const open = openIdx === i;
          return (
            <div key={i} style={{
              borderRadius: 12,
              background: open ? "rgba(200,0,26,.08)" : "transparent",
              border: open ? "1px solid rgba(200,0,26,.25)" : "1px solid rgba(255,255,255,.06)",
              transition: "background .2s, border-color .2s",
            }}>
              <button
                onClick={() => setOpenIdx(open ? -1 : i)}
                style={{
                  width: "100%",
                  background: "none",
                  border: "none",
                  padding: "12px 14px",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  textAlign: "left",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  fontFamily: "var(--fh)",
                }}
              >
                <span style={{ flex: 1, lineHeight: 1.3 }}>{item.q}</span>
                <motion.span
                  animate={{ rotate: open ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    fontSize: 11,
                    color: open ? "#FF5A6E" : "rgba(255,255,255,.5)",
                    flexShrink: 0,
                  }}
                >▼</motion.span>
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    style={{ overflow: "hidden" }}
                  >
                    <div style={{
                      padding: "0 14px 14px",
                      fontSize: 13,
                      color: "rgba(255,255,255,.72)",
                      lineHeight: 1.55,
                    }}>
                      {item.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// === Helper : section narrative qui se révèle au scroll (whileInView) ===
function StorySection({ icon, title, desc, accent = "rgba(200,0,26,.5)", index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.025, borderColor: "rgba(255,255,255,.18)" }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.05 * (index % 2) }}
      style={{
        padding: "22px 20px",
        background: "rgba(255,255,255,.04)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,.08)",
        borderRadius: 18,
        position: "relative",
        overflow: "hidden",
        cursor: "default",
      }}
    >
      <div style={{
        position: "absolute",
        top: -40, right: -40,
        width: 120, height: 120,
        borderRadius: "50%",
        background: `radial-gradient(circle, ${accent} 0%, transparent 70%)`,
        filter: "blur(20px)",
        pointerEvents: "none",
      }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 30, marginBottom: 10 }}>{icon}</div>
        <h3 style={{
          fontFamily: "var(--fh)",
          fontSize: 17,
          fontWeight: 800,
          color: "#fff",
          margin: "0 0 8px",
          lineHeight: 1.25,
          letterSpacing: -0.2,
        }}>{title}</h3>
        <p style={{
          fontSize: 13,
          color: "rgba(255,255,255,.7)",
          margin: 0,
          lineHeight: 1.55,
        }}>{desc}</p>
      </div>
    </motion.div>
  );
}

// === Helper : narratif scroll complet (4 cards) ===
export function StoryScroll({ t }) {
  const sections = [
    { icon: "🛒", title: t.spS1Title, desc: t.spS1Desc, accent: "rgba(200,0,26,.45)" },
    { icon: "🤝", title: t.spS2Title, desc: t.spS2Desc, accent: "rgba(124,58,237,.4)" },
    { icon: "🇨🇭", title: t.spS3Title, desc: t.spS3Desc, accent: "rgba(255,180,80,.35)" },
    { icon: "🚀", title: t.spS4Title, desc: t.spS4Desc, accent: "rgba(34,197,94,.35)" },
  ];
  return (
    <div style={{
      width: "100%",
      maxWidth: 420,
      margin: "0 auto",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      padding: "8px 0 32px",
    }}>
      {sections.map((s, i) => (
        <StorySection key={i} index={i} {...s} />
      ))}
    </div>
  );
}

// === Helper : pluie d'étoiles qui pop pour le splash ===
// 40 étoiles à positions aléatoires. Chacune apparaît rapidement (scale 0→1,3→0,
// opacity 0→1→0), avec délai initial random et repeat infini.
// Palette : blanc 60 % · rouge HC 25 % · doré 15 %.
export function PoppingStars() {
  const stars = useMemo(() =>
    Array.from({ length: 40 }, (_, i) => {
      const palette = Math.random();
      const color = palette < 0.6
        ? { fill: "#fff", glow: "rgba(255,255,255,.95)" }
        : palette < 0.85
          ? { fill: "#FF5A6E", glow: "rgba(255,90,110,.95)" }
          : { fill: "#FFD278", glow: "rgba(255,210,120,.95)" };
      return {
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 3 + Math.random() * 6,
        duration: 1.0 + Math.random() * 1.2,
        repeatDelay: 0.3 + Math.random() * 2.0,
        initialDelay: Math.random() * 1.8,
        rotate: (Math.random() - 0.5) * 50,
        fill: color.fill,
        glow: color.glow,
      };
    }), []);
  return (
    <div style={{
      position: "absolute", inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 0,
    }}>
      {stars.map(s => (
        <motion.div
          key={s.id}
          initial={{ opacity: 0, scale: 0, rotate: s.rotate }}
          animate={{ opacity: [0, 1, 0], scale: [0, 1.3, 0] }}
          transition={{
            duration: s.duration,
            delay: s.initialDelay,
            repeat: Infinity,
            repeatDelay: s.repeatDelay,
            ease: "easeOut",
            times: [0, 0.45, 1],
          }}
          style={{
            position: "absolute",
            top: `${s.y}%`,
            left: `${s.x}%`,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: s.fill,
            boxShadow: `0 0 ${s.size * 3}px ${s.glow}, 0 0 ${s.size * 6}px ${s.glow}`,
            transform: `rotate(${s.rotate}deg)`,
          }}
        />
      ))}
    </div>
  );
}
